if (!String.prototype.toFullName) {
    String.prototype.toFullName = function (lastName) {
        if (this === undefined || this === null) return ''
        if (lastName === undefined || lastName === null) return this
        return this + ' ' + lastName
    }
}

if (!String.prototype.toFamilyContactName) {
    String.prototype.toFamilyContactName = function (contactName) {
        if (this === undefined || this === null) return ''
        if (contactName === undefined || contactName === null) return this
        return this + ', ' + contactName
    }
}

if (!String.prototype.toFilterStartDate) {
    String.prototype.toFilterStartDate = function () {
        const parsedDate = new Date(this)
        if (isNaN(parsedDate.getTime())) {
            throw new Error(`Invalid date string: ${this}`)
        }
        parsedDate.setHours(0, 0, 0, 0)
        return parsedDate
    }
}

if (!String.prototype.toFilterEndDate) {
    String.prototype.toFilterEndDate = function () {
        const parsedDate = new Date(this)
        if (isNaN(parsedDate.getTime())) {
            throw new Error(`Invalid date string: ${this}`)
        }
        parsedDate.setHours(23, 59, 59, 999)
        return parsedDate
    }
}

// --- UTC save helpers (date-only fields) ---
// Many forms use dxDateBox with `type: 'date'` (date-only). When serialized, a JS Date becomes an absolute UTC instant,
// so if we set the "time part" in local time, different user timezones produce different instants.
// These helpers force a consistent UTC day boundary regardless of browser timezone.
if (!Date.prototype.toUtcStartOfDay) {
    Date.prototype.toUtcStartOfDay = function () {
        if (isNaN(this.getTime())) {
            throw new Error(`Invalid Date object: ${this}`)
        }
        const y = this.getFullYear()
        const m = this.getMonth()
        const d = this.getDate()
        return new Date(Date.UTC(y, m, d, 0, 0, 0, 0))
    }
}

if (!Date.prototype.toUtcEndOfDay) {
    Date.prototype.toUtcEndOfDay = function () {
        if (isNaN(this.getTime())) {
            throw new Error(`Invalid Date object: ${this}`)
        }
        const y = this.getFullYear()
        const m = this.getMonth()
        const d = this.getDate()
        return new Date(Date.UTC(y, m, d, 23, 59, 59, 999))
    }
}

const utilServices = {}

utilServices.emptyList = () => {
    return {
        totalCount: 0,
        data: [],
    }
}

utilServices.isNullOrUndefined = (value) => {
    return value === undefined || value === null
}

/**
 * Class/term date-only fields are stored as UTC day boundaries (toUtcStartOfDay / toUtcEndOfDay).
 * End-of-day UTC (e.g. 2026-06-21T23:59:59.999Z) is the next calendar day in timezones east of UTC;
 * map back using the UTC calendar day as `yyyy-MM-dd` (Redux-serializable; safe for dxDateBox).
 */
utilServices.utcStorageInstantToIsoDateString = function utcStorageInstantToIsoDateString(value) {
    if (value == null || value === '') return value
    const d = value instanceof Date ? value : new Date(value)
    if (Number.isNaN(d.getTime())) return value
    const y = d.getUTCFullYear()
    const mo = String(d.getUTCMonth() + 1).padStart(2, '0')
    const day = String(d.getUTCDate()).padStart(2, '0')
    return `${y}-${mo}-${day}`
}

/** @deprecated Prefer utcStorageInstantToIsoDateString for Redux; kept for call sites that need a Date. */
utilServices.utcStorageInstantToLocalDateOnly = function utcStorageInstantToLocalDateOnly(value) {
    if (value == null || value === '') return value
    const d = value instanceof Date ? value : new Date(value)
    if (Number.isNaN(d.getTime())) return value
    return new Date(d.getUTCFullYear(), d.getUTCMonth(), d.getUTCDate())
}

/**
 * Normalizes form/API date-only values to a local-midnight Date for toUtcStartOfDay / toUtcEndOfDay.
 * Handles `yyyy-MM-dd` from Redux mappers and Date from dxDateBox.
 */
utilServices.dateOnlyValueToLocalDate = function dateOnlyValueToLocalDate(value) {
    if (value == null || value === '') return value
    if (value instanceof Date && !Number.isNaN(value.getTime())) return value
    if (typeof value === 'string') {
        const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(value.trim())
        if (m) {
            const y = Number(m[1])
            const mo = Number(m[2]) - 1
            const d = Number(m[3])
            return new Date(y, mo, d)
        }
    }
    const d = new Date(value)
    return Number.isNaN(d.getTime()) ? value : d
}

/**
 * Normalizes a date-only form/Redux value to UTC start-of-day and returns an ISO string
 * so class/term API payloads stay Redux-serializable (no Date in actions).
 */
utilServices.toUtcStartOfDayForApi = function toUtcStartOfDayForApi(value) {
    const d = utilServices.dateOnlyValueToLocalDate(value)
    if (d instanceof Date && !Number.isNaN(d.getTime())) {
        return d.toUtcStartOfDay().toISOString()
    }
    return value
}

/** Same as toUtcStartOfDayForApi for UTC end-of-day (23:59:59.999Z on that calendar day). */
utilServices.toUtcEndOfDayForApi = function toUtcEndOfDayForApi(value) {
    const d = utilServices.dateOnlyValueToLocalDate(value)
    if (d instanceof Date && !Number.isNaN(d.getTime())) {
        return d.toUtcEndOfDay().toISOString()
    }
    return value
}

const CLASS_API_DATE_FIELDS = [
    'dateStart',
    'dateEnd',
    'bookingStart',
    'bookingEnd',
    'termStart',
    'termEnd',
]

/**
 * TagBox can accidentally include the primary teacher id; the API never stores it on additional rows.
 * Keep client `additionalTeacherIds` aligned with server semantics (and dedupe).
 */
utilServices.sanitizeAdditionalTeacherIds = function sanitizeAdditionalTeacherIds(hostRecord, additionalIds) {
    if (!Array.isArray(additionalIds)) return []
    const primaryRaw = hostRecord?.teacherId ?? hostRecord?.TeacherId
    const primary =
        primaryRaw != null && primaryRaw !== '' ? String(primaryRaw).toLowerCase() : null
    const seen = new Set()
    const out = []
    for (const raw of additionalIds) {
        if (raw == null || raw === '') continue
        const s = String(raw)
        const lower = s.toLowerCase()
        if (primary && lower === primary) continue
        if (seen.has(lower)) continue
        seen.add(lower)
        out.push(s)
    }
    return out
}

/** Shallow-clone a class API record with date fields adjusted for date-only editors. */
utilServices.mapClassApiDatesForDateEditors = function mapClassApiDatesForDateEditors(record) {
    if (record == null || typeof record !== 'object') return record
    const next = { ...record }
    for (const key of CLASS_API_DATE_FIELDS) {
        if (Object.prototype.hasOwnProperty.call(next, key) && next[key] != null && next[key] !== '') {
            next[key] = utilServices.utcStorageInstantToIsoDateString(next[key])
        }
    }
    if (Array.isArray(next.additionalTeachers)) {
        const rawIds = next.additionalTeachers
            .map((t) => t?.id ?? t?.Id)
            .filter((id) => id != null && id !== '')
            .map((id) => String(id))
        next.additionalTeacherIds = utilServices.sanitizeAdditionalTeacherIds(next, rawIds)
    } else if (!Array.isArray(next.additionalTeacherIds)) {
        next.additionalTeacherIds = []
    } else {
        next.additionalTeacherIds = utilServices.sanitizeAdditionalTeacherIds(
            next,
            next.additionalTeacherIds.filter((id) => id != null && id !== '').map((id) => String(id))
        )
    }
    return next
}

const TERM_API_DATE_FIELDS = ['dateStart', 'dateEnd']

/** Same as mapClassApiDatesForDateEditors for term records (save uses toUtcStartOfDay / toUtcEndOfDay). */
utilServices.mapTermApiDatesForDateEditors = function mapTermApiDatesForDateEditors(record) {
    if (record == null || typeof record !== 'object') return record
    const next = { ...record }
    for (const key of TERM_API_DATE_FIELDS) {
        if (Object.prototype.hasOwnProperty.call(next, key) && next[key] != null && next[key] !== '') {
            next[key] = utilServices.utcStorageInstantToIsoDateString(next[key])
        }
    }
    return next
}

export default utilServices
